import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getFormByToken, submitLifesaverForm } from '../../services/lifesaverLinkService'
import TextareaWithCounter from '../../components/TextareaWithCounter/TextareaWithCounter'
import './LifesaverForm.css'

function LifesaverForm() {
  const { token } = useParams()
  const { user } = useAuth()
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [isFormCreator, setIsFormCreator] = useState(false)


  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    address: '',
    has_plus_one: false,
    plus_one_first_name: '',
    plus_one_last_name: '',
    plus_one_email: '',
    plus_one_phone: '',
  })

  // Helper function to get couple names from form (only first names)
  const getCoupleNames = (formData) => {
    if (!formData?.weddings?.profiles) {
      console.warn('⚠️ getCoupleNames: No profiles data found')
      return 'THE COUPLE'
    }
    
    const profile = formData.weddings.profiles
    const partner1 = profile.first_name ? profile.first_name.trim() : null
    const partner2 = profile.partner_first_name ? profile.partner_first_name.trim() : null
    
    // Log para diagnóstico (siempre mostrar para debugging)
    console.log('🔍 getCoupleNames - profile completo:', profile)
    console.log('🔍 partner1 (first_name):', partner1)
    console.log('🔍 partner2 (partner_first_name):', partner2)
    console.log('🔍 partner_user_id:', profile.partner_user_id)
    
    if (partner1 && partner2) {
      const result = `${partner1.toUpperCase()} & ${partner2.toUpperCase()}`
      console.log('✅ getCoupleNames resultado:', result)
      return result
    } else if (partner1) {
      console.warn('⚠️ getCoupleNames: Solo se encontró un nombre:', partner1)
      return partner1.toUpperCase()
    }
    
    console.warn('⚠️ getCoupleNames: No se encontraron nombres, usando "THE COUPLE"')
    return 'THE COUPLE'
  }

  // Get field placeholders based on field name
  const getFieldPlaceholder = (fieldName) => {
    const placeholders = {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone Number',
      country: 'Country',
      address: 'Address',
    }
    return placeholders[fieldName] || ''
  }

  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true)
        setError(null)
        
        if (!token) {
          setError('Invalid form link. Please check the URL.')
          setLoading(false)
          return
        }
        
        const formData = await getFormByToken(token)
        
        if (!formData) {
          if (process.env.NODE_ENV === 'development') {
            console.error('LifesaverForm: No form data returned. Possible causes:')
            console.error('1. Form does not exist with slug:', token)
            console.error('2. RLS policies blocking access')
            console.error('3. Form is disabled (enabled = false)')
          }
          setError('Form not found or disabled. Please check the link and try again.')
          setLoading(false)
          return
        }
        
        // Verificar si el usuario actual es el creador del formulario
        const currentUserId = user?.id
        const formCreatorId = formData?.weddings?.user_id
        const userIsCreator = currentUserId && formCreatorId && currentUserId === formCreatorId
        setIsFormCreator(userIsCreator)
        
        // Verificar que el formulario esté habilitado
        // Si el usuario es el creador, mostrar advertencia pero permitir ver el formulario
        // Si no es el creador, bloquear el acceso completamente
        if (!formData.enabled) {
          if (userIsCreator) {
            // El creador puede ver el formulario incluso si está deshabilitado, pero con advertencia
            setError('⚠️ This form is currently disabled. As the form creator, you can view it, but guests cannot access it. Please enable it in your dashboard for guests to use it.')
          } else {
            // Usuarios no creadores no pueden ver formularios deshabilitados
            setError('This form is currently disabled. Please contact the event organizers.')
            setForm(null) // No mostrar el formulario
            setLoading(false)
            return
          }
        }
        
        // Asegurar que form_fields sea un array
        if (!formData.form_fields || !Array.isArray(formData.form_fields)) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('form_fields is not an array, initializing with default fields')
          }
          formData.form_fields = [
            { name: 'first_name', label: 'First Name', required: true, type: 'text' },
            { name: 'last_name', label: 'Last Name', required: true, type: 'text' },
            { name: 'email', label: 'Email', required: true, type: 'email' },
          ]
        }
        
        setForm(formData)
        setError(null)
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading form:', err)
        }
        setError(`Error loading form: ${err.message || 'Please check the link and try again.'}`)
        setForm(null)
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      loadForm()
    } else {
      setError('No form token provided. Please check the link.')
      setLoading(false)
    }
  }, [token])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form) return

    // Validar solo los campos que están marcados como requeridos
    const enabledFields = form.form_fields || []
    const validationErrors = []

    for (const field of enabledFields) {
      if (field.name === 'plus_one') continue // Plus one se valida por separado
      
      // Solo validar campos que tienen un nombre válido
      if (!field.name || field.name.trim() === '') continue
      
      // Solo validar campos que están explícitamente marcados como requeridos
      // O los campos básicos que siempre son requeridos (first_name, last_name, email)
      const isRequired = field.required === true || 
                        field.name === 'first_name' || 
                        field.name === 'last_name' || 
                        field.name === 'email'
      
      if (isRequired) {
        const value = formData[field.name]
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          const fieldLabel = field.label || getFieldPlaceholder(field.name) || field.name
          // Solo agregar el error si el label no está vacío
          if (fieldLabel && fieldLabel.trim() !== '') {
            validationErrors.push(fieldLabel)
          }
        }
      }
    }

    // Validar plus one si está habilitado y marcado
    if (form.form_fields?.some((f) => f.name === 'plus_one') && formData.has_plus_one) {
      if (!formData.plus_one_first_name?.trim()) {
        validationErrors.push('Plus one first name is required')
      }
      if (!formData.plus_one_last_name?.trim()) {
        validationErrors.push('Plus one last name is required')
      }
    }

    if (validationErrors.length > 0) {
      setError(`Please fill in all required fields: ${validationErrors.join(', ')}`)
      return
    }

    // Validación adicional: asegurar que al menos first_name, last_name y email no estén vacíos
    if (!formData.first_name?.trim() || !formData.last_name?.trim() || !formData.email?.trim()) {
      setError('First name, last name, and email are required')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Limpiar formData antes de enviar - solo incluir campos válidos
      const cleanFormData = {
        first_name: formData.first_name?.trim() || '',
        last_name: formData.last_name?.trim() || '',
        email: formData.email?.trim() || '',
        phone: formData.phone?.trim() || '',
        country: formData.country?.trim() || '',
        address: formData.address?.trim() || '',
        has_plus_one: Boolean(formData.has_plus_one), // Asegurar que sea booleano
        plus_one_first_name: formData.plus_one_first_name?.trim() || '',
        plus_one_last_name: formData.plus_one_last_name?.trim() || '',
        plus_one_email: formData.plus_one_email?.trim() || '',
        plus_one_phone: formData.plus_one_phone?.trim() || '',
      }
      
      console.log('🔍 LifesaverForm - Enviando datos limpios:', cleanFormData)
      
      await submitLifesaverForm(token, cleanFormData)
      setSubmitted(true)
    } catch (err) {
      console.error('❌ Error submitting form:', err)
      console.error('❌ Error details:', {
        message: err?.message,
        code: err?.code,
        details: err?.details,
        hint: err?.hint
      })
      // Mostrar mensaje de error más específico si está disponible
      let errorMessage = err?.message || 'Error submitting form. Please try again.'
      
      // Mensajes más amigables para errores comunes
      if (errorMessage.includes('invalid input syntax for type boolean')) {
        errorMessage = 'Form error. Please refresh the page and try again.'
      } else if (errorMessage.includes('22P02')) {
        errorMessage = 'Form data error. Please make sure all fields are filled in correctly.'
      }
      
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="lifesaver-form-page">
        <div className="lifesaver-form-container">
          <div className="lifesaver-loading">Loading form...</div>
        </div>
      </div>
    )
  }

  if (error && !form) {
    return (
      <div className="lifesaver-form-page">
        <div className="lifesaver-form-container">
          <div className="lifesaver-error">
            <h2>Oops!</h2>
            <p>{error}</p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
              If you believe this is an error, please contact the event organizers.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!form) {
    return (
      <div className="lifesaver-form-page">
        <div className="lifesaver-form-container">
          <div className="lifesaver-error">
            <h2>Form not found</h2>
            <p>The form you're looking for doesn't exist or has been disabled.</p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
              Please check the link and try again, or contact the event organizers.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Obtener nombres de la pareja desde profiles (solo si form existe)
  const coupleNames = form ? getCoupleNames(form) : 'THE COUPLE'

  if (submitted) {
    return (
      <div className="lifesaver-form-page">
        <div className="lifesaver-form-container">
          <div className="lifesaver-success">
            <div className="lifesaver-success-icon">✓</div>
            <h2>Thank you!</h2>
            <p>Your information has been submitted successfully.</p>
            <p className="lifesaver-success-names">
              {coupleNames !== 'THE COUPLE' 
                ? coupleNames.replace(' & ', ' & ')
                : 'The couple'}
              {' '}will be in touch soon!
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="lifesaver-form-page">
      <div className="lifesaver-form-container">
        {/* Landing Page View */}
        {!showForm && !submitted && (
          <div className="lifesaver-landing">
            <div className="lifesaver-landing-header">
              <div className="lifesaver-brand-logo">
                <img src="/img/logo-prueba.png" alt="The UNFILTERED COLLECTIVE" className="logo-img" />
              </div>
            </div>

            <div className="lifesaver-illustration">
              <img src="/img/AngelesConChanpagne.png" alt="Angels with Champagne" className="lifesaver-illustration-img" />
            </div>

            <div className="lifesaver-couple-names">{coupleNames}</div>

            <button className="lifesaver-cta-btn" onClick={() => setShowForm(true)}>
              Give 'Em My Info
            </button>

            <p className="lifesaver-privacy">Your details will only be shared with {coupleNames}</p>
          </div>
        )}

        {/* Form View */}
        {showForm && !submitted && (
          <div className="lifesaver-form-view">
            <div className="lifesaver-form-header">
              <div className="lifesaver-brand-logo">
                <img src="/img/logo-prueba.png" alt="The UNFILTERED COLLECTIVE" className="logo-img" />
              </div>
            </div>

            <div className="lifesaver-form-illustration">
              <img src="/img/AngelEnCopa.png" alt="Angel in Glass" className="lifesaver-illustration-img" />
            </div>

            <form onSubmit={handleSubmit} className="lifesaver-form">
              {error && <div className="lifesaver-form-error">{error}</div>}

              {/* Render fields dynamically based on form.form_fields */}
              {(() => {
                // Obtener campos del formulario o usar campos por defecto
                let fieldsToShow = form?.form_fields?.filter(field => {
                  // Excluir plus_one (se maneja por separado)
                  if (field.name === 'plus_one') return false
                  
                  // Solo incluir campos que tengan un nombre válido
                  if (!field.name || field.name.trim() === '') return false
                  
                  // Solo incluir campos conocidos/esperados
                  const validFieldNames = ['first_name', 'last_name', 'email', 'phone', 'country', 'address']
                  return validFieldNames.includes(field.name)
                }) || []
                
                // Si no hay campos, usar los campos básicos por defecto
                if (fieldsToShow.length === 0) {
                  fieldsToShow = [
                    { name: 'first_name', label: 'First Name', required: true, type: 'text' },
                    { name: 'last_name', label: 'Last Name', required: true, type: 'text' },
                    { name: 'email', label: 'Email', required: true, type: 'email' },
                  ]
                }
                
                // Ordenar campos: primero los requeridos, luego los opcionales
                const requiredFields = fieldsToShow.filter(f => f.required || f.name === 'first_name' || f.name === 'last_name' || f.name === 'email')
                const optionalFields = fieldsToShow.filter(f => !f.required && f.name !== 'first_name' && f.name !== 'last_name' && f.name !== 'email')
                fieldsToShow = [...requiredFields, ...optionalFields]
                
                // Eliminar duplicados por nombre
                const seen = new Set()
                fieldsToShow = fieldsToShow.filter(field => {
                  if (seen.has(field.name)) {
                    return false
                  }
                  seen.add(field.name)
                  return true
                })
                
                return fieldsToShow.map((field) => {
                  // Mostrar label para todos los campos
                  const fieldLabel = field.label || getFieldPlaceholder(field.name) || field.name
                  const placeholder = field.placeholder || getFieldPlaceholder(field.name) || field.label || field.name || ''
                  
                  // Si no hay label ni placeholder válido, no mostrar el campo
                  if (!fieldLabel || fieldLabel.trim() === '') {
                    return null
                  }

                  return (
                    <div key={field.name} className="lifesaver-form-group">
                      <label className="lifesaver-field-label">
                        {fieldLabel} {field.required && '*'}
                      </label>
                      {field.type === 'textarea' ? (
                        <TextareaWithCounter
                          name={field.name}
                          value={formData[field.name] || ''}
                          onChange={handleInputChange}
                          required={field.required || false}
                          placeholder={placeholder}
                          rows={4}
                          maxLength={500}
                          className="lifesaver-form-input"
                        />
                      ) : (
                        <input
                          type={field.type || 'text'}
                          name={field.name}
                          value={formData[field.name] || ''}
                          onChange={handleInputChange}
                          required={field.required || false}
                          placeholder={placeholder}
                          className="lifesaver-form-input"
                        />
                      )}
                    </div>
                  )
                }).filter(Boolean) // Eliminar nulls
              })()}

              {/* Plus One Section - only show if plus_one field is enabled */}
              {form?.form_fields?.some((f) => f.name === 'plus_one' && (f.enabled === undefined || f.enabled === true)) && (
                <div className="lifesaver-plus-one-section">
                  <div className="lifesaver-plus-one-toggle">
                    <label>
                      <input
                        type="checkbox"
                        name="has_plus_one"
                        checked={formData.has_plus_one}
                        onChange={handleInputChange}
                      />
                      <span>Plus one in the picture?</span>
                    </label>
                    <p>Add them below so we can save them a seat!</p>
                  </div>

                  {formData.has_plus_one && (
                    <>
                      <div className="lifesaver-form-group">
                        <label>First Name</label>
                        <input
                          type="text"
                          name="plus_one_first_name"
                          value={formData.plus_one_first_name}
                          onChange={handleInputChange}
                          placeholder="Jane"
                        />
                      </div>

                      <div className="lifesaver-form-group">
                        <label>Last Name</label>
                        <input
                          type="text"
                          name="plus_one_last_name"
                          value={formData.plus_one_last_name}
                          onChange={handleInputChange}
                          placeholder="Doe"
                        />
                      </div>

                      <div className="lifesaver-form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="plus_one_email"
                          value={formData.plus_one_email}
                          onChange={handleInputChange}
                          placeholder="jane@example.com"
                        />
                      </div>

                      <div className="lifesaver-form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          name="plus_one_phone"
                          value={formData.plus_one_phone}
                          onChange={handleInputChange}
                          placeholder="+1 234 567 8901"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              <button type="submit" className="lifesaver-submit-btn" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send'}
              </button>

              <p className="lifesaver-privacy">Your details will only be shared with {coupleNames}</p>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}

export default LifesaverForm
